import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import WisdomProgression from "@/components/WisdomProgression";
import TopoBackground from "@/components/TopoBackground";
import Parallax from "@/components/Parallax";
import { MEDIA, MANIFESTO, PRINCIPLES, INITIATIVES, PARTNERS } from "@/data/content";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[680px] w-full overflow-hidden">
        <Parallax strength={80}>
          <img
            data-testid="hero-image"
            src={MEDIA.heroImage}
            alt="Ancient dwelling built into canyon walls — Country as the first knowledge system"
            className="h-full w-full object-cover"
          />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806]/70 via-[#050806]/40 to-[#050806]" />
        <div className="absolute inset-0 bg-[#050806]/30" />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <Label className="mb-6 block">Stewarding humanity's living wisdom</Label>
            <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#F4F1ED]">
              Stewarding Humanity's Living Wisdom
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-[#D8D4CE] leading-relaxed font-light">
              Every generation inherits knowledge. Every generation chooses what will
              endure. WisdomKeepers helps communities steward memory, culture and knowledge
              so future generations inherit more than information — they inherit understanding.
            </p>
            <div className="mt-11 flex flex-col sm:flex-row gap-4">
              <Link
                to="/vision"
                data-testid="hero-explore-button"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
              >
                Explore Our Vision
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/partner"
                data-testid="hero-partner-button"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md border border-white/25 text-[#F4F1ED] hover:border-[#D4A373] hover:text-[#D4A373] transition-colors duration-500"
              >
                Partner With Us
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#A8A5A1] text-xs tracking-[0.3em] uppercase">
          Scroll
        </div>
      </section>

      {/* WISDOM SITS IN PLACES — philosophy below hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <img
          src={MEDIA.placeWide}
          alt="A living landscape at golden light"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806] via-[#050806]/80 to-[#050806]" />
        <TopoBackground opacity={0.06} />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
          <Reveal>
            <Label className="block mb-6">Our organising principle</Label>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.06] text-[#F4F1ED]">
              Wisdom Sits in Places
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 space-y-5 text-lg md:text-xl text-[#C9C5BE] leading-relaxed max-w-2xl mx-auto font-light">
              <p>
                For thousands of generations, people have learned from Country. Stories
                belong to landscapes. Language belongs to place. Ecology belongs to seasons.
                Knowledge is carried through relationship.
              </p>
              <p>
                Wisdom is not simply remembered. It is practiced. It is renewed. It is
                stewarded.
              </p>
              <p>
                WisdomKeepers exists to help communities strengthen these relationships for
                future generations. Technology does not replace Country —{" "}
                <span className="text-[#D4A373]">it helps us return to it.</span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO teaser */}
      <section className="py-28 md:py-40 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.25] text-[#F4F1ED]">
              Archives preserve what was.{" "}
              <span className="text-[#D4A373]">Wisdom stewards what must continue.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl text-lg text-[#A8A5A1] leading-relaxed">
              Memory is not about celebrating the past. It is about understanding it deeply
              enough to make wiser decisions for the future.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/vision"
              data-testid="home-manifesto-link"
              className="mt-8 inline-flex items-center gap-2 text-[#D4A373] hover:gap-3 transition-all duration-300"
            >
              Read the manifesto <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHY WISDOM MATTERS — split */}
      <section className="border-b border-white/5">
        <div className="grid md:grid-cols-2">
          <div className="relative min-h-[420px] md:min-h-[640px] overflow-hidden group">
            <img
              src={MEDIA.archive}
              alt="Fragmented archives and forgotten records"
              className="absolute inset-0 h-full w-full object-cover opacity-45 grayscale group-hover:opacity-55 transition-all duration-1000"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/50 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-14">
              <Label className="mb-4 block">What was</Label>
              <p className="font-display text-2xl md:text-3xl text-[#F4F1ED] leading-snug">
                Fragmented. Disconnected. Poorly governed. Held by institutions rather
                than communities.
              </p>
            </div>
          </div>
          <div className="relative min-h-[420px] md:min-h-[640px] overflow-hidden group">
            <img
              src={MEDIA.community}
              alt="Community using knowledge on Country"
              className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-[1400ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/40 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-14">
              <Label className="mb-4 block">What lives</Label>
              <p className="font-display text-2xl md:text-3xl text-[#F4F1ED] leading-snug">
                Knowledge held by community, used on Country, informing decisions for
                generations.
              </p>
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <Reveal>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#F4F1ED]">
              The challenge is not collecting knowledge.
              <br className="hidden md:block" />
              <span className="text-[#D4A373]"> The challenge is keeping it alive.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-[#A8A5A1] leading-relaxed">
              Around the world, enormous amounts of cultural and ecological knowledge are
              being recorded. Yet much of it remains fragmented and unable to inform decisions
              on Country. Technology alone cannot solve this. Communities can.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PRINCIPLES preview */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">Our Principles</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              Governance before technology. People before databases.
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-lg overflow-hidden">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div
                  data-testid={`principle-card-${i}`}
                  className="h-full bg-[#0A0F0C] p-8 md:p-10 hover:bg-[#101612] transition-colors duration-500 group"
                >
                  <span className="font-display text-2xl text-[#D4A373]/40 group-hover:text-[#D4A373] transition-colors duration-500">
                    0{i + 1}
                  </span>
                  <h3 className="mt-5 font-display text-2xl text-[#F4F1ED]">{p.title}</h3>
                  <p className="mt-3 text-[#A8A5A1] leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STEWARDSHIP INTELLIGENCE teaser */}
      <section className="relative py-28 md:py-40 overflow-hidden border-b border-white/5">
        <img
          src={MEDIA.mist}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-[#050806]/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Label className="block mb-6">A new capacity</Label>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F4F1ED]">
              Stewardship Intelligence
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg md:text-xl text-[#A8A5A1] leading-relaxed">
              Intelligence is not simply the ability to analyse information. Stewardship
              Intelligence is the capacity to make decisions that strengthen people, places
              and future generations.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/stewardship-intelligence"
              data-testid="home-stewardship-link"
              className="mt-8 inline-flex items-center gap-2 text-[#D4A373] hover:gap-3 transition-all duration-300"
            >
              Enter Stewardship Intelligence <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FEATURED INITIATIVES teaser */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <Label className="block mb-4">Featured Initiatives</Label>
                <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED]">
                  Work carried forward, together.
                </h2>
              </div>
              <Link
                to="/initiatives"
                data-testid="home-initiatives-link"
                className="inline-flex items-center gap-2 text-[#D4A373] hover:gap-3 transition-all duration-300"
              >
                View all initiatives <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            {INITIATIVES.slice(0, 3).map((it, i) => (
              <Reveal key={it.name} delay={i * 0.08}>
                <Link
                  to="/initiatives"
                  data-testid={`home-initiative-tile-${i}`}
                  className="group block relative aspect-[4/5] rounded-lg overflow-hidden"
                >
                  <img
                    src={it.img}
                    alt={it.name}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/20 to-transparent" />
                  <div className="relative z-10 h-full flex flex-col justify-end p-7">
                    <span className="wk-label">{it.tag}</span>
                    <h3 className="mt-2 font-display text-3xl text-[#F4F1ED]">{it.name}</h3>
                    <p className="mt-2 text-sm text-[#D8D4CE]/80">{it.body}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH marquee */}
      <section className="py-16 border-b border-white/5 overflow-hidden">
        <Label className="block text-center mb-8">Who we work with</Label>
        <div className="relative flex overflow-hidden">
          <div className="wk-marquee flex shrink-0 gap-12 pr-12">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <span
                key={i}
                className="font-display text-2xl md:text-3xl text-[#A8A5A1]/50 whitespace-nowrap"
              >
                {p} <span className="text-[#D4A373]/40 ml-12">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Recurring philosophy thread */}
      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-center wk-label mb-12">The WisdomKeepers thread</p>
          </Reveal>
          <WisdomProgression dark />
        </div>
      </section>

      {/* CLOSING VISION */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <img
          src={MEDIA.closing}
          alt="Children walking with Elders"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/60 to-[#050806]/30" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-32 text-center">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F4F1ED] leading-tight">
              Restoring Humanity's Relationship With Place
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-[#D8D4CE] leading-relaxed">
              Imagine a future where every community remains connected to the places that
              shaped them. Where language, story, ecology and culture continue to inform
              daily life. Where Artificial Intelligence quietly supports stewardship rather
              than replacing wisdom. Where children learn from Elders. Where restoration
              begins with memory. Where knowledge remains connected to Country. This is the
              future WisdomKeepers exists to help steward.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-11 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/partner"
                data-testid="closing-conversation-button"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
              >
                Start a Conversation
              </Link>
              <Link
                to="/partner"
                data-testid="closing-partner-button"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md border border-white/25 text-[#F4F1ED] hover:border-[#D4A373] hover:text-[#D4A373] transition-colors duration-500"
              >
                Partner With WisdomKeepers
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
